import { isInternalUser } from 'states/profile/business-rules';
import flags from 'containers/flags/service';
import { getEnvironmentSettings } from 'services/environment';

const dimensions = [
    {
        name: 'campaign_id',
        serverName: 'campaign_id',
        label: 'Campaign',
    },
    {
        name: 'ad_id',
        serverName: 'ad_id',
        label: 'Ad',
    },
    {
        name: 'age',
        serverName: 'age',
        label: 'Age',
    },
    {
        name: 'channel_id',
        serverName: 'channel',
        label: 'Channel',
    },
    {
        name: 'date',
        serverName: 'date',
        label: 'Date',
    },
    {
        name: 'day_of_week',
        serverName: 'time',
        label: 'Time',
    },
    {
        name: 'device_os',
        serverName: 'os',
        label: 'Device OS',
    },
    {
        name: 'gender',
        serverName: 'gender',
        label: 'Gender',
    },
    {
        name: 'geo_country',
        serverName: 'country',
        label: 'Country',
    },
    {
        name: 'geo_country_region',
        serverName: 'region',
        label: 'Region',
    },
    {
        name: 'hour',
        serverName: 'hour',
        label: 'Hour',
    },
    {
        name: 'month',
        serverName: 'month',
        label: 'Month',
    },
    {
        name: 'quarter',
        serverName: 'quarter',
        label: 'Quarter',
    },
    {
        name: 'markup_id',
        serverName: 'markup_id',
        label: 'Creative',
    },
    {
        name: 'audience_segment',
        serverName: 'audience_segment',
        label: 'Audience',
    },
    {
        name: 'poi_tag_chosen',
        serverName: 'poi',
        label: 'POI',
    },
    {
        name: 'week_by_campaign_start_day',
        serverName: 'week_by_campaign_start_day',
        label: 'Week by campaign start',
    },
    {
        name: 'week_by_monday_to_sunday',
        serverName: 'week_by_monday_to_sunday',
        label: 'Week by Monday to Sunday',
    },
    {
        name: 'tactic_id',
        serverName: 'tactic_id',
        label: 'Tactic',
    },
    {
        name: 'platform',
        serverName: 'platform',
        label: 'Platform',
    },
    {
        name: 'flight_id',
        serverName: 'flight_id',
        label: 'Flights',
    },
];

// Explore-v2 keeps a curated subset/order of globally available dimensions.
export const REPORT_EXPLORE_V2_BASE_DIMENSION_ORDER = [
    'age',
    'date',
    'day_of_week',
    'device_os',
    'gender',
    'geo_country',
    'geo_country_region',
    'markup_id',
    'audience_segment',
    'hour',
    'month',
    'ad_id',
    'adgroup',
    'tactic_id',
    'platform',
];

export const REPORT_EXPLORE_V2_OPTIONAL_DIMENSION_ORDER = [
    'audience_id',
    'flight_id',
    'geo_city',
    'creative_channel',
    'creative_size',
    'video_length',
    'inventory_id',
    'inventory_name',
    'app_store_cat',
    'pmp_id',
    'pmp_deal_name',
    'inventory_type',
    'exchange_id',
    'video_placement_name',
    'video_position_name',
    'fsa',
    'device_type_name',
    'device_language',
    'device_carrier',
    'conversion_lag',
    'device_model',
    'video_skippable',
    'pmp_deal_source_type',
    'audience_category',
    'audience_subcategory',
    'dooh_venue_type_subclass',
    'dooh_screen_id',
    'dooh_board_name',
    'publisher_id',
    'publisher_name',
];

export const DOOH_DIMENSION_ALLOWLIST = [
    'date',
    'day_of_week',
    'hour',
    'month',
    'quarter',
    'week',
    'week_by_monday_to_sunday',
    'week_by_campaign_start_day',
    'ad_id',
    'markup_id',
    'tactic_id',
    'platform',
    'flight_id',
    'geo_country',
    'geo_country_region',
    'geo_city',
    'fsa',
    'inventory_type',
    'exchange_id',
    'pmp_id',
    'pmp_deal_name',
    'creative_size',
    'creative_channel',
    'dooh_venue_type_subclass',
    'dooh_screen_id',
    'dooh_board_name',
    'publisher_id',
    'publisher_name',
    'adgroup',
    'audience_category',
    'audience_subcategory',
];

export const NEW_DIMENSIONS = [
    'geo_country',
    'geo_country_region',
    'geo_city',
    'fsa',
    'device_type_name',
    'device_language',
    'device_carrier',
    'exchange_id',
    'inventory_type',
    'inventory_id',
    'inventory_name',
    'app_store_cat',
    'pmp_id',
    'pmp_deal_name',
    'creative_size',
    'creative_channel',
    'video_length',
    'video_placement_name',
    'video_position_name',
    'conversion_lag',
    'device_model',
    'pmp_deal_source_type',
    'video_skippable',
    'audience_category',
    'audience_subcategory',
];

export function getDimensions() {
    let newDimensions = [...dimensions];
    const environmentSettings = getEnvironmentSettings();
    const canUseNewDimensions = environmentSettings.useNewReportingDimensions();
    const canUseCTVOrDOOH = environmentSettings.canUseCTVOrDOOH();

    if (isInternalUser()) {
        newDimensions.push({
            name: 'camp_org',
            serverName: 'camp_org',
            label: 'Organization',
        });
        newDimensions.push({
            name: 'audience_id',
            serverName: 'audience_id',
            label: 'Audience ID',
        });
    }

    if (canUseNewDimensions) {
        newDimensions.push({
            name: 'geo_city',
            serverName: 'geo_city',
            label: 'City',
        });
        newDimensions.push({
            name: 'fsa',
            serverName: 'fsa',
            label: 'Postal Code',
        });
        newDimensions.push({
            name: 'device_type_name',
            serverName: 'device_type_name',
            label: 'Device Type',
        });
        newDimensions.push({
            name: 'device_language',
            serverName: 'device_language',
            label: 'Device Language',
        });
        newDimensions.push({
            name: 'device_carrier',
            serverName: 'device_carrier',
            label: 'Device Carrier',
        });
        newDimensions.push({
            name: 'exchange_id',
            serverName: 'exchange_id',
            label: 'Exchange',
        });
        newDimensions.push({
            name: 'inventory_type',
            serverName: 'inventory_type',
            label: 'Environment',
        });
        newDimensions.push({
            name: 'inventory_id',
            serverName: 'inventory_id',
            label: 'App / Site ID',
        });
        newDimensions.push({
            name: 'inventory_name',
            serverName: 'inventory_name',
            label: 'App / Site Name',
        });
        newDimensions.push({
            name: 'app_store_cat',
            serverName: 'app_store_cat',
            label: 'App / Site Category',
        });
        newDimensions.push({
            name: 'pmp_id',
            serverName: 'pmp_id',
            label: flags.isEnabled('efb_35_curated_deals') ? 'Deal ID' : 'Private Deal ID',
        });
        newDimensions.push({
            name: 'pmp_deal_name',
            serverName: 'pmp_deal_name',
            label: flags.isEnabled('efb_35_curated_deals') ? 'Deal Name' : 'Private Deal Name',
        });
        newDimensions.push({
            name: 'creative_size',
            serverName: 'creative_size',
            label: 'Creative Size',
        });
        newDimensions.push({
            name: 'creative_channel',
            serverName: 'creative_channel',
            label: 'Creative Channel',
        });
        newDimensions.push({
            name: 'video_length',
            serverName: 'video_length',
            label: 'Video Length',
        });
        newDimensions.push({
            name: 'video_placement_name',
            serverName: 'video_placement_name',
            label: 'Video Placement',
        });
        newDimensions.push({
            name: 'video_position_name',
            serverName: 'video_position_name',
            label: 'Video Position',
        });
        newDimensions.push({
            name: 'conversion_lag',
            serverName: 'conversion_lag',
            label: 'Conversion Lag',
        });
    }

    if (canUseNewDimensions && flags.isEnabled('efb_17_dooh_target_venue')) {
        newDimensions.push({
            name: 'dooh_venue_type_subclass',
            serverName: 'dooh_venue_type_subclass',
            label: 'Venue Type',
        });
    }

    if (canUseCTVOrDOOH && flags.isEnabled('efb_522_dooh_board_id_reporting')) {
        newDimensions.push({
            name: 'dooh_screen_id',
            serverName: 'dooh_screen_id',
            label: 'DOOH Board ID',
        });
    }

    if (canUseCTVOrDOOH && flags.isEnabled('efb_522_dooh_board_id_reporting')) {
        newDimensions.push({
            name: 'dooh_board_name',
            serverName: 'dooh_board_name',
            label: 'DOOH Board Name',
        });
    }

    if (canUseCTVOrDOOH && flags.isEnabled('efb_522_dooh_board_id_reporting')) {
        newDimensions.push({
            name: 'publisher_id',
            serverName: 'publisher_id',
            label: 'Publisher ID',
        });
    }

    if (canUseCTVOrDOOH && flags.isEnabled('efb_522_dooh_board_id_reporting')) {
        newDimensions.push({
            name: 'publisher_name',
            serverName: 'publisher_name',
            label: 'Publisher Name',
        });
    }

    if (canUseNewDimensions && flags.isEnabled('en_3511_snowflake_dimensions_lower_priority')) {
        newDimensions.push({
            name: 'device_model',
            serverName: 'device_model',
            label: 'Device Model',
        });
        newDimensions.push({
            name: 'pmp_deal_source_type',
            serverName: 'pmp_deal_source_type',
            label: 'Private Deal Source Type',
        });
        newDimensions.push({
            name: 'video_skippable',
            serverName: 'video_skippable',
            label: 'Video Skippable',
        });
        newDimensions.push({
            name: 'audience_category',
            serverName: 'audience_category',
            label: 'Audience Category',
        });
        newDimensions.push({
            name: 'audience_subcategory',
            serverName: 'audience_subcategory',
            label: 'Audience Subcategory',
        });
    }
    return newDimensions;
}

export default dimensions;
